import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { numberShorthand } from '@benzinga/utils';
import { Financials } from '@benzinga/securities-manager';
import { ShortInterest } from '@benzinga/quotes-manager';
import { Thumb } from './OverviewRow';
import { sortByRecentDate } from '../../utils/quote/summaries';
import { BaseQuoteProfile } from '../../utils/quote/types';
import { QuoteTechnicals } from '../../entities/quoteEntity';
import { SectionTitle } from '@benzinga/core-ui';

export interface QuoteMetricRowProps {
  label: string;
  value: string | number | React.ReactElement;
  className?: string;
}

export const QuoteMetricRow = ({ className, label, value }: QuoteMetricRowProps) => {
  return (
    <tr
      className={classNames('flex items-center justify-between w-full py-[0.4rem]', {
        [`${className}`]: !!className,
      })}
    >
      <td className="label text-gray-500">{label}</td>
      <td className="value font-bold">{value}</td>
    </tr>
  );
};

interface QuoteMetricsProps {
  profile: BaseQuoteProfile;
  fundamentals: Financials | null;
  technicals: QuoteTechnicals;
  shortInterest: ShortInterest[] | null;
}

const QuoteMetrics: React.FC<QuoteMetricsProps> = ({ profile, fundamentals, shortInterest, technicals }) => {
  const { t } = useTranslation('quote');
  const daysToCover = Array.isArray(shortInterest) ? sortByRecentDate(shortInterest)?.[0]?.daysToCover : '-';

  const quoteMetrics = [
    {
      label: t('Quote.Metrics.open'),
      value: profile?.richQuoteData?.open ? `$${numberShorthand(profile?.richQuoteData?.open, 2)}` : '-',
    },
    {
      label: t('Quote.Metrics.close'),
      value: profile?.richQuoteData?.close ? `$${numberShorthand(profile?.richQuoteData?.close, 2)}` : '-',
    },
    {
      label: t('Quote.Metrics.volume-avg'),
      value: `${numberShorthand(profile?.richQuoteData?.volume, 2) || '-'} / ${
        numberShorthand(profile?.richQuoteData?.averageVolume, 2) || '-'
      }`,
    },
    {
      label: t('Quote.Metrics.day-range'),
      value: `${profile?.richQuoteData?.low ? `$${numberShorthand(profile?.richQuoteData?.low, 2)}` : '-'} - ${
        profile?.richQuoteData?.high ? `$${numberShorthand(profile?.richQuoteData?.high, 2)}` : '-'
      }`,
    },
    {
      label: t('Quote.Metrics.52wkrange'),
      value: `${numberShorthand(profile?.richQuoteData?.fiftyTwoWeekLow, 2) || '-'} - ${
        numberShorthand(profile?.richQuoteData?.fiftyTwoWeekHigh, 2) || '-'
      }`,
    },
    {
      label: t('Quote.Metrics.market-cap'),
      value: profile?.richQuoteData?.marketCap ? `$${numberShorthand(profile?.richQuoteData?.marketCap, 2)}` : '-',
    },
    {
      label: t('Quote.Metrics.pe-ratio'),
      value: fundamentals?.valuationRatios?.[0]?.peRatio
        ? `${numberShorthand(fundamentals?.valuationRatios?.[0]?.peRatio, 2)}`
        : '-',
    },
    {
      label: t('Quote.Metrics.dividend-yield'),
      value: fundamentals?.valuationRatios?.[0]?.forwardDividendYield
        ? `${numberShorthand(fundamentals?.valuationRatios?.[0]?.forwardDividendYield * 100, 2)}%`
        : '-',
    },
    {
      label: t('Quote.Metrics.exchange'),
      value: profile?.richQuoteData?.bzExchange ?? '-',
    },
    {
      label: t('Quote.Metrics.rsi'),
      value: technicals?.rsi ? (
        <div className="flex items-center gap-4">
          {Math.round(technicals?.rsi)}
          {technicals?.rsi > 70 || technicals?.rsi < 30 ? <Thumb down /> : <Thumb up />}
        </div>
      ) : (
        '-'
      ),
    },
    {
      label: t('Quote.Metrics.short-interest'),
      value: profile?.tickerDetails?.sharesShortPercentOfFloat ? (
        <div className="flex items-center gap-4">
          {profile?.tickerDetails?.sharesShortPercentOfFloat}%{' '}
          {profile?.tickerDetails?.sharesShortPercentOfFloat > 10 ? <Thumb down /> : <Thumb up />}
        </div>
      ) : (
        '-'
      ),
    },
    {
      label: t('Quote.Metrics.days-to-cover'),
      value: daysToCover,
    },
  ];

  return (
    <div className="card p-4">
      <SectionTitle level={2} className="mb-2">
        {t('Quote.Metrics.key-statistics')}
      </SectionTitle>
      {quoteMetrics.map(metric => (
        <div
          className="flex w-full justify-between py-1.5 text-sm items-center border-b border-bzblue-300 last:border-b-0"
          key={metric.label}
        >
          <div className="label text-gray-500">{metric.label}</div>
          <div className="value font-bold text-right">{metric.value}</div>
        </div>
      ))}
    </div>
  );
};

export default QuoteMetrics;
